import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import DeleteModal from "../../Components/Common/DeleteModal";
import {
  Button,
  Col,
  Card,
  FormFeedback,
  Input,
  Label,
  Row,
  CardBody,
  Container,
} from "reactstrap";
import ModalCreate from "../../Components/Common/Modal";
import TableContainer from "../../Components/Common/TableContainer";

import {
  addUser,
  deleteUser,
  getUser,
  updateUser,
} from "../../slices/users/thunk";
import { getRol } from "../../slices/roles/thunks";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { getUserById } from "../../api/modules/users/users.api";

// Spinner Loading Page
const Spinner = ({ isLoading }: { isLoading: boolean }) => (
  isLoading ? (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
      }}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : null
);

const UserList = () => {
  document.title = "Usuarios | Tren Taita Imbabura";

  const dispatch: any = useDispatch();
  const [users, setUsers] = useState<any>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const searchRequestRef = useRef<any>(null);

  // States for password visibility
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getRol());
  }, [dispatch]);

  const handleSearch = useCallback((searchValue: string) => {
    if (searchRequestRef.current) {
      searchRequestRef.current.abort();
    }

    setIsFetching(true);
    const params = searchValue ? { search: searchValue } : undefined;

    const request = dispatch(getUser(params));
    searchRequestRef.current = request;

    request.finally(() => {
      if (searchRequestRef.current === request) {
        setIsFetching(false);
      }
    });
  }, [dispatch]);

  const userLists = useSelector((state: any) => state.Users?.userLists || []);
  const rolLists = useSelector((state: any) => state.Roles?.rolLists || []);

  const roleOptions = useMemo(() => {
    return rolLists.map((role: any) => ({
      value: role.id,
      label: role.name
    }));
  }, [rolLists]);

  // Select styles for React-Select (Corporate Design for Single & Multi)
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: state.isFocused ? '#981c1f' : '#e2e5e8', // Primary brand color vs border light
      boxShadow: state.isFocused ? '#981c1f' : 'none',
      '&:hover': {
        borderColor: '#981c1f',
      },
      borderRadius: '0.25rem',
      padding: '2px',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#495057', // Texto corporativo oscuro
      fontWeight: 500,
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#981c1f', // Fondo vibrante (primary color)
      borderRadius: '4px',
      border: 'none', // Sin borde para evitar ruido visual
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#FFFFFF', // Texto estrictamente blanco
      fontWeight: 600,
      padding: '2px 6px',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#FFFFFF',
      opacity: 0.8,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f06548', // Color danger de bootstrap/velzon al hover
        color: '#FFFFFF',
        opacity: 1,
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#981c1f'
        : state.isFocused
        ? 'rgba(152, 28, 31, 0.1)' // Un fondo muy tenue del color primario al hacer hover
        : undefined,
      color: state.isSelected ? '#ffffff' : '#495057',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#981c1f',
        color: '#ffffff',
      },
    }),
  };

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setUsers(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
    } else {
      setModal(true);
    }
  }, [modal]);

  const onClickDelete = (user: any) => {
    setUsers(user);
    setDeleteModal(true);
  };

  // Delete User
  const handleDeleteUser = async () => {
    setIsLoading(true);
    if (users) {
      await dispatch(deleteUser(users.id));
      setDeleteModal(false);
      validation.resetForm();
      setIsLoading(false);
    }
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (users && users.name) || "",
      email: (users && users.email) || "",
      document: (users && users.document) || "",
      roles: (users && users.roles && users.roles.length > 0 ? (typeof users.roles[0] === 'string' ? users.roles[0] : users.roles[0].id) : ""),
      maxCapacity: (users && users.maxCapacity) || "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().max(200, "Máximo 200 caracteres").required("Por favor ingrese el nombre del usuario"),
      email: Yup.string().email("Email inválido").max(200, "Máximo 200 caracteres").required("Por favor ingrese el email"),
      document: Yup.string().max(20, "Máximo 20 caracteres").required("Por favor ingrese el documento"),
      roles: Yup.string().required("Por favor seleccione un rol"),
      password: isEdit
        ? Yup.string().optional()
        : Yup.string().required("Por favor ingrese una contraseña"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Las contraseñas deben coincidir exactamente')
        .when('password', {
          is: (val: string) => val && val.length > 0,
          then: () => Yup.string().required('Por favor repita su contraseña'),
          otherwise: () => Yup.string().optional()
        }),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      if (isEdit) {
        const updatedForm: any = {
          id: users.id,
          name: values.name,
          email: values.email,
          document: values.document,
          roles: [values.roles],
          maxCapacity: values.maxCapacity
        };
        if (values.password && values.password.trim() !== "") {
          updatedForm.password = values.password;
        }
        await dispatch(updateUser(updatedForm));
        validation.resetForm();
      } else {
        const newUser = {
          name: values.name,
          email: values.email,
          document: values.document,
          roles: [values.roles],
          maxCapacity: values.maxCapacity,
          password: values.password,
        };
        await dispatch(addUser(newUser));
        validation.resetForm();
      }
      toggle();
      setIsLoading(false);
    },
  });

  // Update Data
  const handleCustomerClick = useCallback(
    async (arg: any) => {
      setIsLoading(true);
      try {
        const freshUser = await getUserById(arg.id);
        setUsers(freshUser);
        setIsEdit(true);
        toggle();
      } catch (error) {
        toast.error("Error al obtener los datos del usuario");
      } finally {
        setIsLoading(false);
      }
    },
    [toggle]
  );

  const columns = useMemo(
    () => [
      {
        header: "Nombre",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: "Documento",
        accessorKey: "document",
        enableColumnFilter: false,
      },
      {
        header: "Roles",
        accessorKey: "roles",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const userRoles = cell.row.original.roles;
          if (!userRoles || !Array.isArray(userRoles)) return "";

          return userRoles.map((userRole: any) => {
            const roleId = typeof userRole === 'string' ? userRole : userRole.id;
            const foundRole = rolLists.find((r: any) => r.id === roleId);
            return foundRole ? foundRole.name : roleId;
          }).join(", ");
        }
      },
      {
        header: "Cap. Máx",
        accessorKey: "maxCapacity",
        enableColumnFilter: false,
      },
      {
        header: "Acciones",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <React.Fragment>
              <div className="d-flex flex-row-reverse">
                <div className="flex-grow-1 tasks_name"></div>
                <div className="flex-shrink-0 ms-4">
                  <ul className="list-inline">
                    <li className="list-inline-item">
                      <Button
                        className="btn btn-warning btn-sm"
                        onClick={() => {
                          const userData = cell.row.original;
                          handleCustomerClick(userData);
                        }}
                      >
                        <i className="ri-pencil-fill"></i>
                      </Button>
                    </li>
                    <li className="list-inline-item">
                      <Button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          const userData = cell.row.original;
                          onClickDelete(userData);
                        }}
                      >
                        <i className="ri-delete-bin-fill"></i>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </React.Fragment>
          );
        },
      },
    ],
    [handleCustomerClick, rolLists]
  );

  return (
    <React.Fragment>
      <Spinner isLoading={isLoading || validation.isSubmitting} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <Card>
              <CardBody>
                <div className="d-flex align-items-center mt-4 mb-2">
                  <h5 className="card-title mb-0 flex-grow-1 text-center fw-bold">
                    USUARIOS
                  </h5>
                  <div className="flex-shrink-0">
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-primary add-btn me-1"
                        onClick={() => {
                          setIsEdit(false);
                          validation.resetForm();
                          toggle();
                        }}
                        disabled={isLoading}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>{" "}
                        Agregar usuario
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <TableContainer
                    columns={columns}
                    data={userLists || []}
                    isGlobalFilter={true}
                    customPageSize={10}
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap mb-0"
                    theadClass="table-light"
                    SearchPlaceholder="Buscar usuarios..."
                    isLoading={isLoading}
                    isFetching={isFetching}
                    onSearch={handleSearch}
                  />
                  <ToastContainer closeButton={false} limit={1} />
                </div>
              </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ModalCreate
        title={isEdit ? "Editar Usuario" : "Crear Usuario"}
        show={modal}
        handle={toggle}
        footer={
          <div className="hstack gap-2 justify-content-end">
            <Button
              type="button"
              onClick={() => {
                setModal(false);
                setUsers(null);
                setShowPassword(false);
                setShowConfirmPassword(false);
              }}
              className="btn-light"
              disabled={isLoading || validation.isSubmitting}
            >
              Cerrar
            </Button>
            <button
              type="submit"
              className="btn btn-primary"
              id="add-btn"
              form="user-form"
              disabled={isLoading || validation.isSubmitting}
            >
              {isEdit ? "Actualizar" : "Agregar"}
            </button>
          </div>
        }
      >
          <form
            id="user-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
          <div className="modal-body">
            <Row className="g-3">
              <Col lg={12}>
                <Label for="name" className="form-label">
                  Nombre <span className="text-danger">*</span>
                </Label>
                <Input
                  name="name"
                  id="name"
                  className="form-control"
                  placeholder="Ingrese el nombre"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.name || ""}
                  invalid={!!(validation.touched.name && validation.errors.name)}
                />
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </Col>

              <Col lg={6}>
                <Label for="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </Label>
                <Input
                  name="email"
                  id="email"
                  className="form-control"
                  placeholder="correo@ejemplo.com"
                  type="email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""}
                  invalid={!!(validation.touched.email && validation.errors.email)}
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </Col>

              <Col lg={6}>
                <Label for="document" className="form-label">
                  Documento <span className="text-danger">*</span>
                </Label>
                <Input
                  name="document"
                  id="document"
                  className="form-control"
                  placeholder="Documento de identidad"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.document || ""}
                  invalid={!!(validation.touched.document && validation.errors.document)}
                />
                {validation.touched.document && validation.errors.document ? (
                  <FormFeedback type="invalid">
                    {validation.errors.document}
                  </FormFeedback>
                ) : null}
              </Col>

              <Col lg={6}>
                <Label for="roles" className="form-label">
                  Rol <span className="text-danger">*</span>
                </Label>
                <Select
                  name="roles"
                  isMulti={false}
                  placeholder="Seleccione el rol"
                  options={roleOptions}
                  classNamePrefix="select"
                  styles={customStyles}
                  value={
                    // Manejo dinámico para soportar isMulti true o false sin que reviente el mapeo
                    Array.isArray(validation.values.roles)
                      ? roleOptions.filter((option: any) => validation.values.roles.includes(option.value))
                      : roleOptions.find((option: any) => option.value === validation.values.roles) || null
                  }
                  onChange={(selectedOption: any) => {
                    if (Array.isArray(selectedOption)) {
                      validation.setFieldValue("roles", selectedOption.map((opt: any) => opt.value));
                    } else {
                      validation.setFieldValue("roles", selectedOption ? selectedOption.value : "");
                    }
                  }}
                  onBlur={() => validation.setFieldTouched("roles", true)}
                />
                {validation.touched.roles && validation.errors.roles ? (
                  <div className="text-danger mt-1 fs-12">
                    {validation.errors.roles}
                  </div>
                ) : null}
              </Col>

              <Col lg={6}>
                <Label for="maxCapacity" className="form-label">
                  Capacidad Máxima
                </Label>
                <input
                    name="maxCapacity"
                    id="maxCapacity"
                    className="form-control pe-5"
                    placeholder="Ingrese la capacidad máxima"
                    type="number"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.maxCapacity || ""}
                />
              </Col>

              <Col lg={12}>
                <Label for="password" className="form-label">
                  Contraseña {!isEdit && <span className="text-danger">*</span>}
                </Label>
                <div className="position-relative auth-pass-inputgroup mb-3">
                  <Input
                    name="password"
                    id="password"
                    className="form-control pe-5"
                    placeholder="Ingrese la contraseña"
                    type={showPassword ? "text" : "password"}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.password || ""}
                    invalid={!!(!isEdit && validation.touched.password && validation.errors.password)}
                  />
                  <button
                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                    type="button"
                    id="password-addon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`ri-eye-${showPassword ? 'fill' : 'off-fill'} align-middle`}></i>
                  </button>
                  {isEdit && (
                    <div className="form-text mt-1">
                      Dejar en blanco para mantener la contraseña actual
                    </div>
                  )}
                  {validation.touched.password && validation.errors.password ? (
                    <FormFeedback type="invalid" className="d-block">
                      {validation.errors.password}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <Label for="confirmPassword" className="form-label">
                  Repetir Contraseña {!isEdit && <span className="text-danger">*</span>}
                </Label>
                <div className="position-relative auth-pass-inputgroup mb-3">
                  <Input
                    name="confirmPassword"
                    id="confirmPassword"
                    className="form-control pe-5"
                    placeholder="Repita la contraseña"
                    type={showConfirmPassword ? "text" : "password"}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.confirmPassword || ""}
                    invalid={!!(validation.touched.confirmPassword && validation.errors.confirmPassword)}
                  />
                  <button
                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                    type="button"
                    id="confirm-password-addon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <i className={`ri-eye-${showConfirmPassword ? 'fill' : 'off-fill'} align-middle`}></i>
                  </button>
                  {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                    <FormFeedback type="invalid" className="d-block">
                      {validation.errors.confirmPassword}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>

              </Row>
            </div>
          </form>
        </ModalCreate>
    </React.Fragment>
  );
};
export default UserList;
